/**
 * Minimal fetch wrapper for the Aile Chat chat-api Worker.
 *
 * Backend is `apps/web/kakehashi/api` (Cloudflare Worker, hono).
 * Phase 1 scope: in dev we proxy `/api/*` via vite -> :8787.
 *
 * Auth: Bearer JWT in `localStorage["ailechat:token"]`.
 */
import { queryClient } from "./queryClient";
import { sameTokenScope } from "./session-scope";
import { clearAuthStorage, retireLegacyAuthStorage } from "./storage-migration";

const TOKEN_KEY = "ailechat:token";

export function getToken(): string | null {
  if (typeof localStorage === "undefined") return null;
  return localStorage.getItem(TOKEN_KEY);
}

export function resolveApiUrl(path: string): string {
  const base = (import.meta.env.VITE_API_BASE as string | undefined)?.replace(/\/$/, "");
  return `${base || "/api"}${path.startsWith("/") ? path : `/${path}`}`;
}

type RefreshResult = { token: string } | { response: Response } | null;
const refreshes = new Map<string, Promise<RefreshResult>>();
let lastRefresh: { previous: string | null; next: string } | null = null;
function contextMatches(token: string | null) {
  return sameTokenScope(token,getToken()) || (lastRefresh?.previous===token && lastRefresh.next===getToken());
}
function changedContextResponse() {
  return new Response(JSON.stringify({error:{code:"AUTH_CONTEXT_CHANGED",message:"ワークスペースが切り替わったため、前の操作の表示を取り消しました。"}}),{status:409,headers:{"content-type":"application/json"}});
}

function refreshUnavailable(): RefreshResult {
  return { response: new Response(JSON.stringify({ error: {
    code: "AUTH_REFRESH_UNAVAILABLE", message: "接続を確認して、もう一度お試しください。",
  } }), { status: 503, headers: { "content-type": "application/json" } }) };
}

function refreshSession(originalToken: string | null, originalSession: string | null): Promise<RefreshResult> {
  // Separate logins must never share an in-flight refresh or its rejection.
  const key = JSON.stringify([originalToken, originalSession]);
  const pending = refreshes.get(key);
  if (pending) return pending;
  const stillCurrent = () => getToken() === originalToken && localStorage.getItem("ailechat:auth-session") === originalSession;
  const invalidate = () => {
    if (!stillCurrent()) return;
    setToken(null);
    queryClient.clear();
    window.dispatchEvent(new CustomEvent("ailechat:auth-session-changed", { detail: null }));
    window.dispatchEvent(new CustomEvent("ailechat:session-changed", { detail: null }));
  };
  const rotate = async (): Promise<RefreshResult> => {
    // Another tab may have refreshed or signed out while this one waited for the lock.
    if (!stillCurrent()) return null;
    try {
      const stored = JSON.parse(originalSession || "null");
      if (!stored?.refresh_token) { invalidate(); return null; }
      const refreshed = await fetch(resolveApiUrl("/v1/auth/refresh"), {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: stored.refresh_token }),
      });
      if (!stillCurrent()) return null;
      // Only a definitive rejection ends a remembered session. Offline wakeups,
      // rate limiting and server failures must leave the refresh credential intact.
      if (refreshed.status === 401 || refreshed.status === 403) { invalidate(); return null; }
      if (!refreshed.ok) return { response: refreshed };
      const next = await refreshed.json();
      if (!stillCurrent()) return null;
      if (typeof next?.access_token !== "string" || !next.access_token ||
          typeof next.refresh_token !== "string" || !next.refresh_token) return refreshUnavailable();
      localStorage.setItem("ailechat:auth-session", JSON.stringify({ ...next, stored_at: Date.now() }));
      setToken(next.access_token);
      lastRefresh = { previous: originalToken, next: next.access_token };
      const session = JSON.parse(localStorage.getItem("ailechat:session") || "null");
      if (session) {
        session.token = next.access_token;
        localStorage.setItem("ailechat:session", JSON.stringify(session));
        window.dispatchEvent(new CustomEvent("ailechat:session-changed", { detail: session }));
      }
      window.dispatchEvent(new CustomEvent("ailechat:auth-session-changed", { detail: next }));
      return { token: next.access_token };
    } catch { return refreshUnavailable(); }
  };
  // Web Locks coordinate the one-use refresh credential across tabs/WebViews.
  // Older engines still deduplicate requests within this document.
  const promise = (async () => typeof navigator !== "undefined" && navigator.locks?.request
    ? await navigator.locks.request("ailechat:session-refresh", rotate)
    : await rotate())().catch(refreshUnavailable).finally(() => { refreshes.delete(key); });
  refreshes.set(key, promise);
  return promise;
}

/** All feature clients share the same bearer token and refresh lifecycle. */
export async function authenticatedFetch(input: RequestInfo | URL, init: RequestInit = {}): Promise<Response> {
  const send = async (token: string | null) => {
    const headers = new Headers(init.headers);
    if (token) headers.set("Authorization", `Bearer ${token}`);
    else headers.delete("Authorization");
    const response=await fetch(input, { ...init, headers });
    return response.status!==401 && !contextMatches(token) ? changedContextResponse() : response;
  };
  const originalToken = getToken();
  const originalSession = localStorage.getItem("ailechat:auth-session");
  const response = await send(originalToken);
  if (response.status !== 401) return response;
  // A delayed 401 may belong to the token another request already refreshed.
  // Never replay an old operation under a separately signed-in identity.
  if (getToken() !== originalToken) {
    return lastRefresh?.previous === originalToken && lastRefresh.next === getToken() ? send(lastRefresh.next) : response;
  }
  const result = await refreshSession(originalToken, originalSession);
  if (result && "token" in result && getToken() === result.token) return send(result.token);
  if (result && "response" in result && getToken() === originalToken) return result.response.clone();
  return response;
}

export function setToken(token: string | null): void {
  if (typeof localStorage === "undefined") return;
  if (token) { retireLegacyAuthStorage(); localStorage.setItem(TOKEN_KEY, token); }
  else clearAuthStorage();
}

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, message: string, body?: unknown) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

interface ApiOptions extends RequestInit {
  /** When true, do not attach Bearer token even if present. */
  anonymous?: boolean;
}

export async function api<T = unknown>(
  path: string,
  options: ApiOptions = {},
): Promise<T> {
  const { anonymous, headers, ...rest } = options;
  const finalHeaders: Record<string, string> = {
    Accept: "application/json",
    "Content-Type": "application/json",
    ...(headers as Record<string, string> | undefined),
  };
  const token = getToken();
  if (!anonymous && token) {
    finalHeaders.Authorization = `Bearer ${token}`;
  }
  // 本番ビルドでは VITE_API_BASE = 'https://chat-api.aile-ai.jp'
  // を指定して Worker を直接叩く。dev は vite proxy `/api` -> :8787。
  const url = resolveApiUrl(path);

  const res = await (anonymous ? fetch : authenticatedFetch)(url, {
    credentials: "same-origin",
    ...rest,
    headers: finalHeaders,
  });

  if (!res.ok) {
    let body: unknown = undefined;
    try {
      body = await res.json();
    } catch {
      /* ignore */
    }
    const detail = body as { error?: string | { message?: string }; message?: string } | undefined;
    throw new ApiError(
      res.status,
      (typeof detail?.error === "object" ? detail.error.message : detail?.message) || `API ${rest.method ?? "GET"} ${path} failed: ${res.status}`,
      body,
    );
  }
  // 204 / empty
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  if (!anonymous && !contextMatches(token)) throw new ApiError(409,"ワークスペースが切り替わったため、前の操作の表示を取り消しました。",{error:{code:"AUTH_CONTEXT_CHANGED"}});
  if (!text) return undefined as T;
  return JSON.parse(text) as T;
}

/** Convenience helpers. */
export const apiGet = <T>(p: string) => api<T>(p);
export const apiPost = <T>(p: string, body: unknown) =>
  api<T>(p, { method: "POST", body: JSON.stringify(body) });
export const apiPatch = <T>(p: string, body: unknown) =>
  api<T>(p, { method: "PATCH", body: JSON.stringify(body) });
export const apiPut = <T>(p: string, body: unknown) =>
  api<T>(p, { method: "PUT", body: JSON.stringify(body) });
export const apiDelete = <T>(p: string) => api<T>(p, { method: "DELETE" });
